import React from 'react';
import PropTypes from 'prop-types';
import { LuSearch, LuTrash2 } from 'react-icons/lu';
import useSmoText from '../smoText';

// Same colour bands as the Excel template the team fills
export const BANDS = {
  identification: 'tw-bg-[#4472c4]',
  schedule: 'tw-bg-[#548235]',
  travelBefore: 'tw-bg-[#bf8f00]',
  visit: 'tw-bg-[#c55a11]',
  travelAfter: 'tw-bg-[#7030a0]',
  issue: 'tw-bg-[#c00000]',
  correction: 'tw-bg-[#00b0f0]',
  source: 'tw-bg-[#00b050]',
  outcome: 'tw-bg-[#7f7f7f]',
  actions: 'tw-bg-[#333333]',
};

const CELL_BASE = 'tw-w-full tw-rounded-lg tw-border tw-border-transparent tw-bg-transparent tw-px-2 tw-py-1.5 tw-text-xs tw-text-slate-800 placeholder:tw-text-slate-400 hover:tw-border-slate-200 focus:tw-border-teal-500 focus:tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-teal-500/25 disabled:tw-cursor-not-allowed disabled:tw-opacity-50 dark:tw-text-slate-100 dark:hover:tw-border-white/10 dark:focus:tw-bg-white/[0.06] dark:[color-scheme:dark]';
const WIDTHS = { text: 'tw-min-w-[8rem]', date: 'tw-min-w-[8.5rem]', time: 'tw-min-w-[6rem]', small: 'tw-min-w-[4rem]', number: 'tw-min-w-[5rem]' };

/** Input cell. `size` only sets the width (date, time, small, number, text). */
export const CellInput = ({ size = 'text', ...rest }) => <input className={`${CELL_BASE} ${WIDTHS[size]} tw-tabular-nums`} {...rest} />;
CellInput.propTypes = { size: PropTypes.oneOf(Object.keys(WIDTHS)) };

export const CellSelect = ({ options, className = '', ...rest }) => (
  <select className={`${CELL_BASE} tw-min-w-[6.5rem] tw-cursor-pointer [&>option]:tw-bg-white [&>option]:tw-text-slate-800 dark:[&>option]:tw-bg-[#1f232b] dark:[&>option]:tw-text-slate-100 ${className}`} {...rest}>
    {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
  </select>
);
CellSelect.propTypes = { options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.node })).isRequired, className: PropTypes.string };

export const CellTextarea = (props) => <textarea className={`${CELL_BASE} tw-min-w-[12rem] tw-resize-y`} rows={1} {...props} />;

export const CellText = ({ className = '', children }) => <span className={`tw-block tw-whitespace-nowrap tw-px-2 tw-text-xs tw-text-slate-700 dark:tw-text-slate-200 ${className}`}>{children}</span>;
CellText.propTypes = { className: PropTypes.string, children: PropTypes.node };

const FILTER_BASE = 'tw-h-7 tw-w-full tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-px-1.5 tw-text-[11px] tw-font-normal tw-normal-case tw-tracking-normal tw-text-slate-700 focus:tw-border-teal-500 focus:tw-outline-none dark:tw-border-white/10 dark:tw-bg-white/[0.05] dark:tw-text-slate-200 dark:[color-scheme:dark]';

/** Filter control of one column (text / date range / select). */
const ColumnFilter = ({ filter, filters, setFilter }) => {
  const { label } = useSmoText();
  if (!filter) return null;
  if (filter.type === 'text') {
    return <input className={FILTER_BASE} placeholder={label('entry.search')} value={filters[filter.key]} onChange={(e) => setFilter(filter.key, e.target.value)} />;
  }
  if (filter.type === 'select') {
    return (
      <select className={FILTER_BASE} value={filters[filter.key]} onChange={(e) => setFilter(filter.key, e.target.value)}>
        {filter.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    );
  }
  return (
    <div className="tw-flex tw-min-w-[15rem] tw-items-center tw-gap-1">
      <input type="date" aria-label={label('dateRange.start')} className={FILTER_BASE} value={filters[filter.from]} onChange={(e) => setFilter(filter.from, e.target.value)} />
      <span aria-hidden="true" className="tw-text-slate-400">—</span>
      <input type="date" aria-label={label('dateRange.end')} className={FILTER_BASE} value={filters[filter.to]} onChange={(e) => setFilter(filter.to, e.target.value)} />
    </div>
  );
};

ColumnFilter.propTypes = { filter: PropTypes.object, filters: PropTypes.object.isRequired, setFilter: PropTypes.func.isRequired };

/**
 * Spreadsheet-like editor of the manual data entry: coloured group bands,
 * column headers (the magnifier opens a filter row) and one editable row per
 * visit. Rows are `{ row, originalIndex }` so edits go to the right record
 * while a filter is on.
 */
const EntryGrid = ({ groups, columns, rows, showFilters, toggleFilters, filters, setFilter, onRemove }) => {
  const { label } = useSmoText();
  return (
    <table className="tw-min-w-full tw-border-separate tw-border-spacing-0 tw-text-xs">
      <thead className="tw-sticky tw-top-0 tw-z-10">
        {/* Group header row (Excel color bands) */}
        <tr>
          <th rowSpan={showFilters ? 3 : 2} className={`tw-sticky tw-left-0 tw-z-20 tw-min-w-[3.5rem] tw-px-2 tw-py-2 tw-text-center tw-font-semibold tw-text-white ${BANDS.actions}`}>#</th>
          {groups.map((group) => (
            <th key={group.key} colSpan={group.span} className={`tw-whitespace-nowrap tw-border-l tw-border-white/30 tw-px-2 tw-py-2 tw-text-center tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-white ${BANDS[group.band]}`}>
              {group.label}
            </th>
          ))}
          <th rowSpan={showFilters ? 3 : 2} className={`tw-min-w-[3rem] tw-px-2 tw-py-2 tw-text-center tw-font-semibold tw-text-white ${BANDS.actions}`}>{label('entry.delete')}</th>
        </tr>
        {/* Column headers */}
        <tr>
          {columns.map((column) => (
            <th key={column.key} className="tw-whitespace-nowrap tw-border-b tw-border-l tw-border-slate-200 tw-bg-slate-100 tw-px-2 tw-py-2 tw-text-left tw-text-[11px] tw-font-semibold tw-text-slate-700 dark:tw-border-white/10 dark:tw-bg-[#262a32] dark:tw-text-slate-200">
              <span className="tw-inline-flex tw-items-center tw-gap-1.5">
                {column.label}
                {column.filter && (
                  <button type="button" onClick={toggleFilters} title={label('entry.filterHint')} aria-label={label('entry.filterHint')} aria-pressed={showFilters} className={`tw-grid tw-h-5 tw-w-5 tw-place-items-center tw-rounded tw-bg-transparent ${showFilters ? 'tw-text-teal-600 dark:tw-text-teal-300' : 'tw-text-slate-400 hover:tw-text-slate-700 dark:hover:tw-text-white'}`}>
                    <LuSearch aria-hidden="true" className="tw-h-3 tw-w-3" />
                  </button>
                )}
              </span>
            </th>
          ))}
        </tr>
        {/* Filter row */}
        {showFilters && (
          <tr>
            {columns.map((column) => (
              <th key={column.key} className="tw-border-b tw-border-l tw-border-slate-200 tw-bg-slate-50 tw-px-1.5 tw-py-1.5 dark:tw-border-white/10 dark:tw-bg-[#22262d]">
                <ColumnFilter filter={column.filter} filters={filters} setFilter={setFilter} />
              </th>
            ))}
          </tr>
        )}
      </thead>
      <tbody>
        {rows.map(({ row, originalIndex }) => (
          <tr key={originalIndex} className="tw-group">
            <td className="tw-sticky tw-left-0 tw-z-[1] tw-border-b tw-border-slate-200 tw-bg-white tw-px-2 tw-py-1.5 tw-text-center tw-tabular-nums tw-text-slate-500 group-hover:tw-bg-slate-50 dark:tw-border-white/10 dark:tw-bg-[#1f232b] dark:group-hover:tw-bg-[#242830]">
              <span className="tw-block tw-font-semibold">{originalIndex + 1}</span>
              <span className={`tw-mt-0.5 tw-inline-block tw-rounded tw-px-1 tw-text-[9px] tw-font-bold tw-uppercase ${row._isNew ? 'tw-bg-amber-400/25 tw-text-amber-800 dark:tw-text-amber-200' : 'tw-bg-emerald-500/15 tw-text-emerald-800 dark:tw-text-emerald-200'}`}>
                {row._isNew ? label('entry.new') : label('entry.existing')}
              </span>
            </td>
            {columns.map((column) => (
              <td key={column.key} className="tw-border-b tw-border-l tw-border-slate-200 tw-bg-white tw-p-1 group-hover:tw-bg-slate-50 dark:tw-border-white/10 dark:tw-bg-[#1f232b] dark:group-hover:tw-bg-[#242830]">
                {column.render(row, originalIndex)}
              </td>
            ))}
            <td className="tw-border-b tw-border-l tw-border-slate-200 tw-bg-white tw-p-1 tw-text-center group-hover:tw-bg-slate-50 dark:tw-border-white/10 dark:tw-bg-[#1f232b] dark:group-hover:tw-bg-[#242830]">
              <button type="button" onClick={() => onRemove(originalIndex)} title={label('entry.removeRow')} aria-label={label('entry.removeRow')} className="tw-grid tw-h-7 tw-w-7 tw-place-items-center tw-rounded-lg tw-bg-transparent tw-text-rose-500 hover:tw-bg-rose-500/10">
                <LuTrash2 aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

EntryGrid.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, label: PropTypes.string, span: PropTypes.number, band: PropTypes.string })).isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, label: PropTypes.string, filter: PropTypes.object, render: PropTypes.func })).isRequired,
  rows: PropTypes.array.isRequired,
  showFilters: PropTypes.bool,
  toggleFilters: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  setFilter: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default EntryGrid;
